import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useChild } from './ChildContext';
import { Database, CourseDoc } from '../services/Database';

interface CoursesContextType {
    courses: CourseDoc[];
    loading: boolean;
    /** 重新取得課程，silent=true 時不顯示 loading；僅在初次載入或 children 變更時需要 */
    refreshCourses: (opts?: { silent?: boolean }) => Promise<void>;
    /** 樂觀更新單一課程（立即更新 UI，不必等重新取得） */
    patchCourse: (childId: string, courseId: string, patch: Partial<CourseDoc>) => void;
    /** 新增課程到 state（寫入成功後呼叫，避免全量 refresh） */
    addCourseToState: (course: CourseDoc) => void;
    /** 從 state 移除單一課程（刪除成功後呼叫） */
    removeCourseFromState: (childId: string, courseId: string) => void;
    /** 從 state 移除某孩子的所有課程（刪除孩子後呼叫） */
    removeCoursesByChildId: (childId: string) => void;
}

const CoursesContext = createContext<CoursesContextType>({
    courses: [],
    loading: true,
    refreshCourses: async () => {},
    patchCourse: () => {},
    addCourseToState: () => {},
    removeCourseFromState: () => {},
    removeCoursesByChildId: () => {},
});

export const CoursesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const { children: childList, loading: childLoading } = useChild();
    const [courses, setCourses] = useState<CourseDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const refreshNonceRef = useRef(0);

    const refreshCourses = useCallback(async (opts?: { silent?: boolean }) => {
        if (!currentUser) {
            setCourses([]);
            setLoading(false);
            return;
        }
        const myNonce = ++refreshNonceRef.current;
        if (!opts?.silent) setLoading(true);
        try {
            // 使用 ChildContext 的 children 避免重複讀取 userSettings（省 1 次 Firestore read）
            const list = await Database.getCourses(null, childLoading ? undefined : childList);
            if (myNonce === refreshNonceRef.current) setCourses(list);
        } catch (e) {
            console.error('刷新課程失敗:', e);
            if (myNonce === refreshNonceRef.current) setCourses([]);
        } finally {
            if (myNonce === refreshNonceRef.current && !opts?.silent) setLoading(false);
        }
    }, [currentUser, childLoading, childList]);

    const patchCourse = useCallback((childId: string, courseId: string, patch: Partial<CourseDoc>) => {
        setCourses(prev => prev.map(c =>
            c.childId === childId && c.id === courseId ? { ...c, ...patch } : c
        ));
    }, []);

    const addCourseToState = useCallback((course: CourseDoc) => {
        setCourses(prev => {
            if (course.id && prev.some(c => c.childId === course.childId && c.id === course.id)) return prev;
            return [...prev, course];
        });
    }, []);

    const removeCourseFromState = useCallback((childId: string, courseId: string) => {
        setCourses(prev => prev.filter(c => !(c.childId === childId && c.id === courseId)));
    }, []);

    const removeCoursesByChildId = useCallback((childId: string) => {
        setCourses(prev => prev.filter(c => c.childId !== childId));
    }, []);

    const childIds = childList.map(c => c.id).join(',');
    useEffect(() => {
        if (!currentUser) {
            setCourses([]);
            setLoading(false);
            return;
        }
        if (childLoading) return;
        refreshCourses();
        // childIds 變更時（如新增/刪除孩子）也需重取課程
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.uid, childLoading, childIds]);

    const value = {
        courses,
        loading,
        refreshCourses,
        patchCourse,
        addCourseToState,
        removeCourseFromState,
        removeCoursesByChildId,
    };

    return (
        <CoursesContext.Provider value={value}>
            {children}
        </CoursesContext.Provider>
    );
};

export const useCourses = () => useContext(CoursesContext);
